import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, Columns3, Filter as FilterIcon } from 'lucide-react';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import UsersFilterModule from 'features/activity/components/UsersFilterModule';
import {
  activitySearchInputClass,
  activitySearchWrapperClass,
  activityToolbarButtonClass,
} from 'features/activity/styles/dataTableStyles';

function formatGroupLabel(group_key) {
  return group_key
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Users page toolbar — matches SessionsFilterBar / EventsFilterBar.
 *
 * Layout:
 *   [Users dropdown? · time period · totals chip] [search 240px] [Add filter] ··· [Edit columns]
 *
 * The Users/Group dropdown sits inline with the toolbar (no separate title
 * row — the global breadcrumb handles the page title). When no groups
 * exist, the dropdown is replaced by nothing (the breadcrumb already says
 * "Users").
 */
const UsersFilterBar = ({
  onTimeFilterChange,
  onSearchChange,
  searchTerm = '',
  selectedTimePreset = 'today',
  onEditColumns = () => {},
  filters = [],
  onFiltersChange = () => {},
  teamId = null,
  // Group dropdown props
  groupKeys = [],
  activeGroupTab = null,
  onGroupTabChange = () => {},
  // Optional totals chip injected by the parent
  totalsSlot = null,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => { setLocalSearchTerm(searchTerm); }, [searchTerm]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onDoc = (e) => {
      if (!dropdownRef.current?.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dropdownOpen]);

  const handleSearch = (value) => {
    const trimmed = (value || '').trim();
    setLocalSearchTerm(trimmed);
    onSearchChange(trimmed);
  };
  const handleSearchClear = () => {
    setLocalSearchTerm('');
    onSearchChange('');
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const activeLabel = activeGroupTab === null ? 'Users' : formatGroupLabel(activeGroupTab);

  return (
    <div
      className="flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Group selector (only when groups exist) — same chip height as the
            time filter so all toolbar pills line up. */}
        {groupKeys.length > 0 ? (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
            >
              {activeLabel}
              <ChevronDown
                size={13}
                strokeWidth={2}
                className="text-[color:var(--text-muted)] transition-transform"
                style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 z-50 mt-1 min-w-[160px] overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg">
                <button
                  type="button"
                  onClick={() => { onGroupTabChange(null); setDropdownOpen(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
                >
                  <span className="flex w-4 flex-shrink-0 items-center justify-center">
                    {activeGroupTab === null ? (
                      <Check size={12} strokeWidth={2.5} className="text-[color:var(--brand-primary)]" />
                    ) : null}
                  </span>
                  Users
                </button>
                {groupKeys.map(({ group_key }) => (
                  <button
                    key={group_key}
                    type="button"
                    onClick={() => { onGroupTabChange(group_key); setDropdownOpen(false); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
                  >
                    <span className="flex w-4 flex-shrink-0 items-center justify-center">
                      {activeGroupTab === group_key ? (
                        <Check size={12} strokeWidth={2.5} className="text-[color:var(--brand-primary)]" />
                      ) : null}
                    </span>
                    {formatGroupLabel(group_key)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <EventsTimeFilter onTimeFilterChange={onTimeFilterChange} selectedPreset={selectedTimePreset} />
        {totalsSlot}

        <div className={`${activitySearchWrapperClass} w-[240px]`}>
          <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search user ID"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleSearch(e.target.value || '');
              }
            }}
            className={activitySearchInputClass}
          />
          {localSearchTerm && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
              aria-label="Clear search"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className={activityToolbarButtonClass}
        >
          <FilterIcon size={14} color="var(--text-muted)" />
          Filter
          {filters.length > 0 && (
            <span className="rounded-full bg-surface-hover px-1.5 text-[10px] font-semibold text-[color:var(--text-primary)]">
              {filters.length}
            </span>
          )}
        </button>

        <div className="ml-auto">
          <button
            type="button"
            onClick={onEditColumns}
            className={activityToolbarButtonClass}
            title="Edit columns"
          >
            <Columns3 size={14} className="text-[color:var(--text-muted)]" />
            Edit columns
          </button>
        </div>
      </div>

      {/* Filters panel (collapsible) */}
      <div
        className={`bg-surface py-4 transition-all duration-200 ${showFilters ? 'block' : 'hidden'}`}
      >
        <UsersFilterModule
          filters={filters}
          onFiltersChange={onFiltersChange}
          teamId={teamId}
        />
      </div>
    </div>
  );
};

export default UsersFilterBar;
